<template>
  <span v-if="validModalValue" :class="ns.b()">
    <span v-if="showPrefix" :class="ns.e('prefix')">
      <slot name="prefix">{{ prefix }}</slot>
    </span>
    <span :class="ns.e('text')">
      {{ formatString }}
    </span>
    <span v-if="showAffix" :class="ns.e('affix')">
      <slot name="affix">{{ affix }}</slot>
    </span>
  </span>
  <span v-else-if="raw">
    {{ modelValue || defaultText }}
  </span>
</template>

<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue'
import { isNumber, isString, isUndefined } from '@element-plus/utils'
import { numberalPattern, useNamespace, useNumberal } from '@element-plus/hooks'
import { numberalProps } from './numberal'

defineOptions({
  name: 'ElNumberal',
})

const props = defineProps(numberalProps)
const slots = useSlots()

const ns = useNamespace('numberal')

const formatString = ref('')
const decimalString = ref('')
const integerString = ref('')
const localString = ref('')
const localNumber = ref(0)
const validModalValue = ref(false)
const showPrefix = computed(() => !!props.prefix || !slots.prefix)
const showAffix = computed(() => !!props.affix || !slots.affix)

const toClear = (): void => {
  localString.value = ''
  localNumber.value = 0
  toClearFormat()
}

const toClearFormat = (): void => {
  formatString.value = ''
  integerString.value = ''
  decimalString.value = ''
}

const isValidNumber = (val?: string | number): boolean => {
  if (isUndefined(val) || (!isString(val) && !isNumber(val))) return false
  const num = Number.parseFloat(val.toString())
  if (Number.isNaN(num)) return false
  if (num.toString().length !== val.toString().length) return false
  return true
}

const toNumberal = (): void => {
  toNotmalize()
  toFormat()
}

const toNotmalize = (): void => {
  validModalValue.value = isValidNumber(props.modelValue)
  if (validModalValue.value) {
    localString.value = isNumber(props.modelValue)
      ? props.modelValue.toString()
      : props.modelValue || '0'
    localNumber.value = Number.parseFloat(localString.value)
  } else {
    toClear()
  }
}

const toFormat = (): void => {
  if (
    !localString.value ||
    !props.format ||
    !numberalPattern.test(props.format)
  ) {
    toClearFormat()
    return
  }

  const { integer, decimal, numberal } = useNumberal(
    localNumber.value,
    props.format
  )

  integerString.value = integer
  decimalString.value = decimal
  formatString.value = numberal
}

watch(
  () => [props.modelValue],
  () => {
    toNumberal()
  },
  {
    immediate: true,
  }
)

watch(
  () => [props.format],
  () => {
    toFormat()
  }
)
</script>
